import React, {
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { DynamicContext } from "../../../Context/DynamicContext";
import recentHistoryIcon from "../../../Assets/Images/recent-history-header-icon.png";
import recentHistoryDarkIcon from "../../../Assets/Images/recent-history-header-dark-icon.png";
import Button from "../../../Common/Button/Button";
import { useNavigate } from "react-router-dom";
import "./UserDashboard.css";
import {
  VYLABS_BASE_URL,
  GET_CANDIDATE_BY_ID,
  VENDORS_SEARCH,
  RECENT_TRACKS,
  ADMIN_RECENT_TRACK,
  UPDATE_CANDIDATE_DETAILS,
  GET_VENDOR_DETAILS_BY_ID,
} from "../../../helper/url_helper";
import { get, post } from "../../../helper/Api_helper";
import RecentTrackCard from "./RecentTrackCard";
import AnimationPlayer from "../../../Common/Animation-Player/AnimationPlayer";
import NoHistoryAnimation from "../../../Assets/Json/No-History-Animation.json";
import SidePopup from "../../../Common/SidePopup/SidePopup";
import CandidateDetailsContainer from "../../../Common/Candidate-Details-Container/CandidateDetailsContainer";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import { fetchWithAuth } from "./FetchWithAuth.js";
import DynamicSkeleton from "../../../Common/Skeleton/Skeleton";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import StageFilterPopup from "../../../Common/StageFilterPopup/StageFilterPopup.js";
import { CALL_STAGE_COLORS } from "../../../Common/constants/stageColors.js";
import backArrowIcon from "../../../Assets/Images/arrow-back-icon.png";
import filterIcon from "../../../Assets/Images/filter-16-light.svg";
import filterDarkIcon from "../../../Assets/Images/filter-16-dark.svg";

function UserRecentTrack() {
  const navigate = useNavigate();
  const { isDarkMode, userDetails ,AdminAccess} = useContext(DynamicContext);
  const [isLoading, setIsLoading] = useState(true);
  const [isCandidateModalOpen, setIsCandidateModalOpen] = useState(false);
  const [selectedCandidateForModal, setSelectedCandidateForModal] =
    useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [isEditingCandidate, setIsEditingCandidate] = useState(false);
  const [originalCandidateData, setOriginalCandidateData] = useState(null);
  const [editedCandidateData, setEditedCandidateData] = useState(null);
  const [emailError, setEmailError] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [vendorList, setVendorList] = useState([]);
  const [vendorOptionLoading, setVendorOptionLoading] = useState(false);
  const [assignedVendor, setAssignedVendor] = useState(null);
  const [todaysTracks, setTodayTracked] = useState([]);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [offset, setOffset] = useState(0);
  const limit = 20;
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isFilterPopupOpen, setIsFilterPopupOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState([]);
  const observer = useRef();
  const allStages = useMemo(() => Object.keys(CALL_STAGE_COLORS || {}), []);
  const filtersRef = useRef({
    selectedUser: null,
    stage: "",
    fromDate: null,
    toDate: null,
  });
  const fetchRecentTrackedCandidates = useCallback(async (newOffset = 0) => {
    try {
      if (newOffset === 0) {
        setIsLoading(true);
      } else {
        setIsLoadingMore(true);
      }

      let response;
      const userId = userDetails?.id;

      if (AdminAccess) {
        const {
          selectedUser,
          stage,
          fromDate: fDate,
          toDate: tDate,
        } = filtersRef.current;
        const queryParams = new URLSearchParams({
          userId: selectedUser || userId || "",
          tenantId: userDetails?.tenant_id || "",
          stage: stage || "",
          fromDate: fDate ? moment(fDate).format("YYYY-MM-DD") : "",
          toDate: tDate ? moment(tDate).format("YYYY-MM-DD") : "",
          limit: String(limit),
          offset: String(newOffset),
        });
        let query = `${ADMIN_RECENT_TRACK}?${queryParams.toString()}`;
        response = await fetchWithAuth(query, { method: "GET" });

        const recentTracksData = (response.recentTracks || []).map((track) => {
          try {
            const parsed = JSON.parse(track.new_value);
            return { ...parsed, ...track };
          } catch {
            return { ...track, new_value: {} };
          }
        });

        if (newOffset === 0) {
          setTodayTracked(recentTracksData);
        } else {
          setTodayTracked((prev) => [...prev, ...recentTracksData]);
        }
      } else {
        const { stage, fromDate: fDate,toDate: tDate, } = filtersRef.current;

        let query = `${RECENT_TRACKS}?userId=${userId}&offset=${newOffset}&limit=${limit}`;

        if (stage) query += `&stage=${encodeURIComponent(stage)}`;

        if (fDate) query += `&fromDate=${moment(fDate).format("YYYY-MM-DD")}`;

        if (tDate) query += `&toDate=${moment(tDate).format("YYYY-MM-DD")}`;
        response = await fetchWithAuth(query, { method: "GET" });
        const recentTracksData = (response.recentTracks || []).flatMap(
          (track) => {
            try {
              const parsed = JSON.parse(track.new_value);
              return Array.isArray(parsed) ? parsed : [parsed];
            } catch {
              return [];
            }
          },
        );
        if (newOffset === 0) {
          setTodayTracked(recentTracksData);
        } else {
          setTodayTracked((prev) => [...prev, ...recentTracksData]);
        }
      }

      setHasMore((response.recentTracks || []).length === limit);
    } catch (error) {
      console.error("fetchRecentTrackedCandidates", error);
      setHasMore(false);
    } finally {
      if (newOffset === 0) setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [limit, userDetails?.id, userDetails?.role, userDetails?.tenant_id]);

  const lastTrackCardRef = useCallback(
    (node) => {
      if (isLoadingMore) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
            const nextOffset = offset + limit;
            setOffset(nextOffset);
            fetchRecentTrackedCandidates(nextOffset);
          }
        },
        { root: null, rootMargin: "200px", threshold: 0.1 },
      );
      if (node) observer.current.observe(node);
    },
    [fetchRecentTrackedCandidates, isLoadingMore, hasMore, offset, limit],
  );

  useEffect(() => {
    setOffset(0);
    fetchRecentTrackedCandidates(0);
  }, [fetchRecentTrackedCandidates]);

  useEffect(() => {
    const userFilter = activeFilters.find((f) => f.type === "user_id");
    const stageFilter = activeFilters.find((f) => f.type === "call_stage");
    filtersRef.current = {
      selectedUser: userFilter?.value || null,
      stage: stageFilter?.value || "",
      fromDate,
      toDate,
    };
  }, [activeFilters, fromDate, toDate]);

  const fetchVendors = async (inputValue = "") => {
    try {
      setVendorOptionLoading(true);
      const response = await get(
        VYLABS_BASE_URL,
        `${VENDORS_SEARCH}?vendor_name=${encodeURIComponent(inputValue)}`,
      );
      const { vendors = [] } = response;
      const fetchedVendors = Array.isArray(vendors)
        ? vendors
            .filter((v) => v?.id && v?.name)
            .map((v) => ({
              label: v.contact_person
                ? `${v.name} - ${v.contact_person}`
                : v.name,
              value: v.id,
            }))
        : [];
      setVendorList(fetchedVendors);
    } catch (err) {
      console.error("fetchVendors error", err);
      setVendorList([]);
    } finally {
      setVendorOptionLoading(false);
    }
  };

  const handleVendorInputChange = (_, value) => {
    if (value && value.length >= 3) {
      fetchVendors(value);
    } else {
      setVendorList([]);
    }
  };

  const fetchCandidateByIdForModal = useCallback(async (id) => {
    try {
      const res = await get(VYLABS_BASE_URL, `${GET_CANDIDATE_BY_ID}?id=${id}`);
      const candidate = res?.candidate;
      const candidatefiles = res?.files;
      const parsedResumeData = candidate?.parsed_resume
        ? JSON.parse(candidate.parsed_resume)
        : null;
      const presignedUrl =
        candidatefiles && candidatefiles.length > 0
          ? candidatefiles[0].presigned_url
          : null;

      const updatedCandidate = {
        ...candidate,
        secondaryEmail: candidate.secondary_email,
        secondaryPhone: candidate.secondary_phone,
        vendorID: candidate.vendor_id,
        parsedResumeData,
        files: candidatefiles || [],
        presignedUrl,
      };
      setSelectedCandidateForModal(updatedCandidate);
      setOriginalCandidateData(updatedCandidate);
      setEditedCandidateData(updatedCandidate);
      setIsCandidateModalOpen(true);
    } catch (error) {
      console.error("fetchCandidateByIdForModal error", error);
      setSelectedCandidateForModal(null);
      setIsCandidateModalOpen(false);
    }
  }, []);

  const handleViewClick = (candidateId) => {
    fetchCandidateByIdForModal(candidateId);
  };

  const closeCandidateModal = () => {
    setIsCandidateModalOpen(false);
    setSelectedCandidateForModal(null);
    setActiveTab(0);
    setIsEditingCandidate(false);
    setEditedCandidateData(null);
    setEmailError("");
    setEmailTouched(false);
    setVendorList([]);
    setAssignedVendor(null);
  };

  const handleTabChange = (tabIndex) => {
    setActiveTab(tabIndex);
  };

  const handleEditCandidate = () => {
    const getValidExperience = (value) => {
      const num = Number(value);

      if (isNaN(num)) return "";
      if (num < 0) return 0;
      return num;
    };

    let experienceValue = "";
    const directExp = getValidExperience(
      selectedCandidateForModal?.years_of_experience,
    );

    const parsedExp = getValidExperience(
      selectedCandidateForModal?.parsedResumeData?.BasicInfo?.YearsOfExperience,
    );

    if (directExp !== "") {
      experienceValue = directExp;
    } else if (parsedExp !== "") {
      experienceValue = parsedExp;
    }
    const candidateWithFormattedSkills = {
      ...selectedCandidateForModal,
      name:
        selectedCandidateForModal.name ||
        selectedCandidateForModal.parsedResumeData?.BasicInfo?.Name ||
        "",
      email:
        selectedCandidateForModal.email ||
        selectedCandidateForModal.parsedResumeData?.BasicInfo?.Email ||
        "",
      secondaryEmail:
        selectedCandidateForModal.secondaryEmail ||
        selectedCandidateForModal.parsedResumeData?.BasicInfo?.secondaryEmail ||
        "",
      phone:
        selectedCandidateForModal.phone ||
        selectedCandidateForModal.parsedResumeData?.BasicInfo?.PhoneNumber ||
        "",
      secondaryPhone:
        selectedCandidateForModal.secondaryPhone ||
        selectedCandidateForModal.parsedResumeData?.BasicInfo?.secondaryPhone ||
        "",
      city:
        selectedCandidateForModal.city ||
        selectedCandidateForModal.parsedResumeData?.BasicInfo
          ?.CurrentLocationCity ||
        "",
      state:
        selectedCandidateForModal.state ||
        selectedCandidateForModal.parsedResumeData?.BasicInfo
          ?.CurrentLocationState ||
        "",
      country:
        selectedCandidateForModal.country ||
        selectedCandidateForModal.parsedResumeData?.BasicInfo
          ?.CurrentLocationCountry ||
        "",
      skills: Array.isArray(selectedCandidateForModal.skills)
        ? selectedCandidateForModal.skills
        : selectedCandidateForModal.parsedResumeData?.Skills || [],
      vendorID:
        selectedCandidateForModal.vendorID ||
        selectedCandidateForModal.parsedResumeData?.BasicInfo?.vendorID ||
        "",
      designation:
        selectedCandidateForModal.designation?.trim() ||
        selectedCandidateForModal.parsedResumeData?.CareerHistory
          ?.JobDetails?.[0]?.JobTitle ||
        "",
      experience: experienceValue,
      lineAddress1:
        selectedCandidateForModal.lineAddress1 ||
        selectedCandidateForModal.parsedResumeData?.BasicInfo?.LineAddress1 ||
        "",
      lineAddress2:
        selectedCandidateForModal.lineAddress2 ||
        selectedCandidateForModal.parsedResumeData?.BasicInfo?.LineAddress2 ||
        "",
      zipCode:
        selectedCandidateForModal.zipCode ||
        selectedCandidateForModal.parsedResumeData?.BasicInfo?.ZipCode ||
        "",
      preferredSalary:
        selectedCandidateForModal.preferredSalary ||
        selectedCandidateForModal.parsedResumeData?.BasicInfo
          ?.PreferredSalary ||
        "",
    };
    setOriginalCandidateData({ ...selectedCandidateForModal });
    setEditedCandidateData(candidateWithFormattedSkills);
    setIsEditingCandidate(true);
    setEmailError("");
    setEmailTouched(false);
  };

  const saveCandidateChanges = async () => {
    if (!editedCandidateData) {
      toast.error("No changes to save");
      return;
    }

    const email =
      editedCandidateData.email !== undefined
        ? editedCandidateData.email
        : selectedCandidateForModal.email || "";
    if (!email.trim()) {
      setEmailError("Email is required");
      setEmailTouched(true);
      toast.error("Email is required");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError("Invalid email format");
      setEmailTouched(true);
      toast.error("Please fill all required fields correctly");
      return;
    }

    setEmailError("");
    setEmailTouched(false);

    try {
      const candidateData = { id: selectedCandidateForModal.id };

      Object.keys(editedCandidateData).forEach((key) => {
        const newValue = editedCandidateData[key];
        const oldValue = selectedCandidateForModal[key];

        const isChanged = Array.isArray(newValue)
          ? JSON.stringify(newValue) !== JSON.stringify(oldValue)
          : newValue !== oldValue;

        if (
          isChanged &&
          newValue !== undefined &&
          newValue !== null &&
          newValue !== ""
        ) {
          candidateData[key] = newValue;
        }
      });

      if (Object.keys(candidateData).length === 1) {
        toast.error("No changes to save");
        return;
      }
      Object.keys(candidateData).forEach((key) => {
        if (key === "preferredSalary") {
          candidateData[key] = Number(candidateData[key]);
        }
      });

      if (candidateData.experience !== undefined) {
        candidateData.yearsOfExperience = Number(candidateData.experience);
        delete candidateData.experience;
      }

      const response = await post(
        VYLABS_BASE_URL,
        UPDATE_CANDIDATE_DETAILS,
        candidateData,
      );

      if (response) {
        const updatedCandidate = {
          ...selectedCandidateForModal,
          ...candidateData,
        };

        if (
          candidateData.vendorID !== undefined &&
          candidateData.vendorID !== selectedCandidateForModal.vendorID
        ) {
          if (candidateData.vendorID) {
            try {
              const vendorResponse = await get(
                VYLABS_BASE_URL,
                `${GET_VENDOR_DETAILS_BY_ID}?id=${candidateData.vendorID}`,
              );
              updatedCandidate.vendorDetails = vendorResponse?.vendor || null;
              setAssignedVendor(vendorResponse);
            } catch (err) {
              console.error("Error fetching vendor details:", err);
              updatedCandidate.vendorDetails = null;
              setAssignedVendor(null);
            }
          } else {
            updatedCandidate.vendorDetails = null;
          }
        }

        setSelectedCandidateForModal(updatedCandidate);
        setIsEditingCandidate(false);
        setOriginalCandidateData(null);
        setEditedCandidateData(null);
        toast.success("Candidate updated successfully");

        setTodayTracked((prevTracks) =>
          prevTracks.map((track) => {
            if (
              track.candidate_id === updatedCandidate.id ||
              track.id === updatedCandidate.id
            ) {
              return {
                ...track,
                candidate: {
                  ...track.candidate,
                  name: updatedCandidate.name,
                },
              };
            }
            return track;
          }),
        );
      }
    } catch (error) {
      console.error("Error updating candidate:", error);
      const backendMessage =
        error?.response?.data?.message ||
        (typeof error?.response?.data === "string"
          ? error.response.data
          : null) ||
        error.message ||
        "Failed to update candidate";
      toast.error(backendMessage);
    }
  };

  const resetCandidateEdit = () => {
    if (originalCandidateData) {
      setSelectedCandidateForModal(originalCandidateData);
    }
    setIsEditingCandidate(false);
    setOriginalCandidateData(null);
    setEditedCandidateData(null);
    setEmailError("");
    setEmailTouched(false);
  };

  const handleCandidateChange = (data) => {
    setEditedCandidateData((prev) => ({
      ...prev,
      ...data,
    }));
  };

  const openFilterPopup = (event) => {
    event.stopPropagation();
    setIsFilterPopupOpen(true);
  };

  const applyStageFilters = (filtersWithValues) => {
    const nextFilters = Array.isArray(filtersWithValues)
      ? filtersWithValues
      : [];
    const userFilter = nextFilters.find((f) => f.type === "user_id");
    const stageFilter = nextFilters.find((f) => f.type === "call_stage");
    const fromDateFilter = nextFilters.find((f) => f.type === "from_date");
    const toDateFilter = nextFilters.find((f) => f.type === "to_date");
    const nextFromDate = fromDateFilter?.value || null;
    const nextToDate = toDateFilter?.value || null;

    if ((nextFromDate && !nextToDate) || (!nextFromDate && nextToDate)) {
      toast.error("Please select both 'From date' and 'To date'.");
      return false;
    }

    if (
      nextFromDate &&
      nextToDate &&
      moment(nextFromDate).isAfter(moment(nextToDate))
    ) {
      toast.error("'From date' cannot be after 'To date'.");
      return false;
    }

    filtersRef.current = {
      selectedUser: userFilter?.value || null,
      stage: stageFilter?.value || "",
      fromDate: nextFromDate,
      toDate: nextToDate,
    };
    setFromDate(nextFromDate);
    setToDate(nextToDate);
    setActiveFilters(nextFilters);
    setTodayTracked([]);
    setHasMore(true);
    setOffset(0);
    setIsFilterPopupOpen(false);
    fetchRecentTrackedCandidates(0);
    return true;
  };

  return (
    <div style={{ minHeight: "100vh" }}>
      <div className="header-main-container">
        <div className="recent-title-header-button-container">
          <div className="header-icon-title-container">
            <img
              alt=""
              src={isDarkMode ? recentHistoryDarkIcon : recentHistoryIcon}
              className="header-title-icon"
            />
            <h2 className="header-container">Recent Track History</h2>
          </div>
          <div className="recent-button-container">
            <Button
              label="Back To Dashboard"
              event={() => navigate("/dashboard")}
              iconSrc={backArrowIcon}
            />
          </div>
        </div>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          margin: "25px",
          gap: "10px",
        }}
      >
        <div style={{ marginLeft: "auto" }}>
          <Button
            iconSrc={isDarkMode ? filterDarkIcon : filterIcon}
            event={openFilterPopup}
            className="filter-stages-btn"
            backgroundColor="transparent"
            color="var(--header__color)"
            border="1px solid var(--border__color)"
            iconWidth="20px"
            style={{
              width: "42px",
              height: "42px",
              padding: "8px",
              borderRadius: "50%",
            }}
          />
        </div>
      </div>
      <StageFilterPopup
        isOpen={isFilterPopupOpen}
        initialFilters={activeFilters}
        stages={allStages}
        onApplyFull={applyStageFilters}
        onClose={() => setIsFilterPopupOpen(false)}
        isInitialLoading={isLoading}
        isLoadingMore={isLoadingMore}
        isDarkMode={isDarkMode}
        popupClassName="recent-track-filter-popup"
        subtitle="Create filters to narrow recent tracks"
        showCandidateViewSelection={false}
        showDateFilter={false}
        showDateRangeFilter={true}
        fromDate={fromDate}
        toDate={toDate}
        includeUserFilterWithoutMode={AdminAccess}
        isCallStageActive={false}
      />
      {isLoading ? (
        <div className="recent-history-loader">
          <div
            style={{
              gridColumn: "1 / -1",
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "20px",
              width: "100%",
              padding: "30px",
              marginTop: "200px",
            }}
          >
            {Array.from({ length: 10 }).map((_, index) => (
              <div key={index}>
                <DynamicSkeleton
                  variant="rectangular"
                  dimensions={{
                    width: "100%",
                    height: "150px",
                    sx: { borderRadius: "16px" },
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      ) : todaysTracks.length === 0 ? (
        <div className="no-recent-tracks-found">
          <div className="no-tracking-history-text">
            No recent tracked candidates found..!!
          </div>
          <AnimationPlayer
            animationSrc={NoHistoryAnimation}
            playerHeight="200px"
            playerWidth="300px"
          />
        </div>
      ) : (
        <div>
          <div className="track-card-container">
            {todaysTracks &&
              todaysTracks.length > 0 &&
              todaysTracks.map((track, idx) => {
                const isLast = idx === todaysTracks.length - 1;
                return (
                  <RecentTrackCard
                    ref={isLast ? lastTrackCardRef : null}
                    key={`${track.id || track.candidate_id || idx}-${idx}`}
                    candidateName={track?.candidate?.name || "N/A"}
                    candidateEmail={track?.candidate?.email || "N/A"}
                    candidatePhone={track?.candidate?.phone || "N/A"}
                    date={new Date(track.updated_at).toLocaleString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                    time={new Date(track.updated_at).toLocaleString("en-US", {
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                    })}
                    firstName={track.first_name}
                    lastName={track.last_name}
                    stage={track?.stage || "N/A"}
                    candidateId={track.candidate_id || track.id}
                    viewEvent={handleViewClick}
                  />
                );
              })}
            {isLoadingMore && (
              <div
                style={{
                  gridColumn: "1 / -1",
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "20px",
                  width: "100%",
                }}
              >
                {Array.from({ length: 4 }).map((_, index) => (
                  <div key={index}>
                    <DynamicSkeleton
                      variant="rectangular"
                      dimensions={{
                        width: "100%",
                        height: "170px",
                        sx: { borderRadius: "16px" },
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
            {!isLoading && todaysTracks.length === 0 && (
              <div className="no-tracking-history">
                <div>
                  <AnimationPlayer
                    animationSrc={NoHistoryAnimation}
                    playerHeight="200px"
                    playerWidth="300px"
                  />
                </div>
                <div className="no-tracking-history-text">
                  No tracks found today.
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {isCandidateModalOpen && selectedCandidateForModal && (
        <SidePopup
          isOpen={isCandidateModalOpen}
          onClose={closeCandidateModal}
          title="Candidate Details"
          width="80%"
        >
          <CandidateDetailsContainer
            candidate={selectedCandidateForModal}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            handleTabChange={handleTabChange}
            hasSearched={false}
            isEditingCandidate={isEditingCandidate}
            setIsEditingCandidate={setIsEditingCandidate}
            handleEditCandidate={handleEditCandidate}
            saveCandidateChanges={saveCandidateChanges}
            resetCandidateEdit={resetCandidateEdit}
            handleCandidateChange={handleCandidateChange}
            editedCandidateData={editedCandidateData}
            emailError={emailError}
            setEmailError={setEmailError}
            emailTouched={emailTouched}
            setEmailTouched={setEmailTouched}
            vendorList={vendorList}
            vendorOptionLoading={vendorOptionLoading}
            handleVendorInputChange={handleVendorInputChange}
            assignedVendor={assignedVendor}
          />
        </SidePopup>
      )}
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
      />
    </div>
  );
}

export default UserRecentTrack;
